import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../config/api';
import type { ApiResponse, SystemSetting } from '../../types';

interface SettingsState {
  settings: Record<string, SystemSetting[]>;
  loading: boolean;
  error: string | null;
}

const initialState: SettingsState = {
  settings: {},
  loading: false,
  error: null,
};

// ─── Async Thunks ─────────────────────────────────────────────────────────

export const fetchSettingsByCategory = createAsyncThunk<
  { category: string; settings: SystemSetting[] },
  string,
  { rejectValue: string }
>('settings/fetchByCategory', async (category, { rejectWithValue }) => {
  try {
    const response = await api.get<ApiResponse<SystemSetting[]>>(
      `/settings/${encodeURIComponent(category)}`
    );
    if (!response.data.success || !response.data.data) {
      return rejectWithValue(response.data.message || 'Failed to fetch settings');
    }
    return { category, settings: response.data.data };
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch settings');
  }
});

export const upsertSetting = createAsyncThunk<
  SystemSetting,
  { category: string; key: string; value: string; isEncrypted?: boolean },
  { rejectValue: string }
>('settings/upsert', async (payload, { rejectWithValue }) => {
  try {
    const response = await api.put<ApiResponse<SystemSetting>>('/settings', payload);
    if (!response.data.success || !response.data.data) {
      return rejectWithValue(response.data.message || 'Failed to save setting');
    }
    return response.data.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    return rejectWithValue(err.response?.data?.message || 'Failed to save setting');
  }
});

// ─── Slice ────────────────────────────────────────────────────────────────

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchSettingsByCategory
    builder
      .addCase(fetchSettingsByCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSettingsByCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.settings[action.payload.category] = action.payload.settings;
      })
      .addCase(fetchSettingsByCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Error fetching settings';
      });

    // upsertSetting
    builder
      .addCase(upsertSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(upsertSetting.fulfilled, (state, action) => {
        state.loading = false;
        const category = action.payload.category;
        const existing = state.settings[category] || [];
        const idx = existing.findIndex((s) => s.key === action.payload.key);
        if (idx >= 0) {
          existing[idx] = action.payload;
        } else {
          existing.push(action.payload);
        }
        state.settings[category] = existing;
      })
      .addCase(upsertSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Error saving setting';
      });
  },
});

export const { clearError } = settingsSlice.actions;
export default settingsSlice.reducer;
